/**
 * SparqlEditor.tsx
 *
 * Read-only SPARQL query editor for advanced ontology exploration
 * Behind ENABLE_SPARQL_QUERIES feature flag
 */

import React, { useState } from 'react';

interface SparqlEditorProps {
  jobId: string;
  onClose: () => void;
}

export function SparqlEditor({ jobId, onClose }: SparqlEditorProps) {
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [results, setResults] = useState<{ head: { vars: string[] }; results: { bindings: Array<Record<string, { value: string }>> } } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const executeQuery = async () => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await fetch(`/analysis/${jobId}/sparql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setResults(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Query execution failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sparql-editor" style={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '800px',
      maxHeight: '80vh',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
      overflow: 'hidden',
      zIndex: 2000
    }}>
      {/* Header */}
      <div className="editor-header" style={{
        padding: '16px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
            SPARQL Query Editor
          </h3>
          <p style={{ fontSize: '12px', color: '#6b7280' }}>
            Read-only queries against the ontology graph
          </p>
        </div>

        <button
          className="btn-close"
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Query Editor */}
      <div className="editor-body" style={{
        padding: '16px',
        maxHeight: 'calc(80vh - 140px)',
        overflowY: 'auto'
      }}>
        <div className="query-input" style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '600',
            marginBottom: '8px',
            color: '#374151'
          }}>
            SPARQL Query
          </label>
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: '100%',
              minHeight: '200px',
              padding: '12px',
              fontFamily: 'monospace',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              resize: 'vertical'
            }}
            placeholder="Enter SPARQL query..."
          />
        </div>

        <div className="editor-actions" style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '16px'
        }}>
          <button
            onClick={executeQuery}
            disabled={loading || !query.trim()}
            style={{
              padding: '8px 16px',
              backgroundColor: loading ? '#9ca3af' : '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Executing...' : 'Execute Query'}
          </button>

          <button
            onClick={() => setQuery(DEFAULT_QUERY)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#ffffff',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Reset to Example
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message" style={{
            padding: '12px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            <p style={{ fontSize: '13px', color: '#dc2626', fontWeight: '500' }}>
              Error: {error}
            </p>
          </div>
        )}

        {/* Results Display */}
        {results && (
          <div className="results-container">
            <h4 style={{
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '12px',
              color: '#374151'
            }}>
              Results ({results.results?.bindings?.length || 0} rows)
            </h4>

            {results.results?.bindings?.length > 0 ? (
              <div style={{
                overflowX: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '4px'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '12px'
                }}>
                  <thead style={{
                    backgroundColor: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <tr>
                      {results.head?.vars?.map((varName: string) => (
                        <th key={varName} style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#374151'
                        }}>
                          {varName}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.results.bindings.map((row: Record<string, { value: string }>, idx: number) => (
                      <tr key={idx} style={{
                        borderBottom: '1px solid #f3f4f6'
                      }}>
                        {results.head.vars.map((varName: string) => (
                          <td key={varName} style={{
                            padding: '8px 12px',
                            color: '#4b5563'
                          }}>
                            {row[varName]?.value || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ fontSize: '13px', color: '#6b7280', padding: '12px' }}>
                No results found
              </p>
            )}
          </div>
        )}

        {/* Security Notice */}
        <div className="security-notice" style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: '4px'
        }}>
          <p style={{ fontSize: '11px', color: '#92400e' }}>
            <strong>Security:</strong> Only SELECT queries are permitted.
            INSERT, DELETE, CLEAR, DROP, CREATE, and LOAD operations are blocked.
          </p>
        </div>
      </div>
    </div>
  );
}

// Default example query
const DEFAULT_QUERY = `PREFIX fair: <https://fair-compass.org/ontology#>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>

SELECT ?rule ?label ?pillar
WHERE {
  ?rule a fair:FAIRRule .
  ?rule rdfs:label ?label .
  ?rule fair:pillar ?pillar .
}
ORDER BY ?pillar ?rule
LIMIT 20`;
